import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Filter as FilterIcon, MoreVertical, Trash2, Bot } from 'lucide-react'
import styled from 'styled-components'
import {
  SubHeaderContainer,
  LeftSide,
  RightSide,
  SectionName,
  TabsDiv,
  Tab,
  RefreshIcon,
  RefreshIconContainer,
  FilterWrapper,
  OptionsButton,
  ButtonCreate,
} from '../styles/subheader-style'
import refreshAct from '../../assets/refresh-active.svg'
import FilterMenu from '../SubHeaderFilter/FilterMenu'
import AppliedFiltersDisplay from '../SubHeaderFilter/AppliedFiltersDisplay'
import { PageHeaderProps } from './types'
import { useTableExportContext } from './TableExportContext'
import { HeaderExportButton } from './styles'
import { useSearch } from '../../contexts/SearchContext'
import DeletedRecordsModal from './DeletedRecordsModal'

const HeaderMenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`

const HeaderMenuDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 200px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  padding: 6px 0;
`

const HeaderMenuItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #333333;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }

  svg {
    flex-shrink: 0;
  }
`

const ExportMenuWrapper = styled.div`
  position: relative;
  display: inline-flex;
`

const ExportDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: 170px;
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  padding: 6px 0;
`

const ExportDropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #333333;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f5f5f5;
  }
`

const resolveTableFromPath = (pathname: string): string => {
  const p = (pathname || '').toLowerCase()
  if (p.includes('/resource') || p.includes('/insumo') || p.includes('/aviamento')) return 'Resource'
  if (p.includes('/tax-scenario') || p.includes('/cenario-fiscal')) return 'TaxScenario'
  if (p.includes('/rnc') || p.includes('/occurrence') || p.includes('/ocorrencia')) return 'Occurrence'
  if (p.includes('/params') || p.includes('/parametro')) return 'Structure_Param'
  if (p.includes('/production-fashion') || p.includes('/production_fashion') || p.includes('/production-flow') || p.includes('/production') || p.includes('/producao')) return 'Services'
  if (p.includes('/composition') || p.includes('/ficha-tecnica')) return 'Product'
  if (p.includes('/collection') || p.includes('/colecao')) return 'Collection'
  if (p.includes('/product-final') || p.includes('/produto-final')) return 'Product'
  if (p.includes('/product') || p.includes('/produto')) return 'Product'
  if (p.includes('/stock') || p.includes('/estoque')) return 'Stock'
  if (p.includes('/certificate') || p.includes('/certificado')) return 'Certificate'
  if (p.includes('/compan') || p.includes('/empresa') || p.includes('/fornecedor') || p.includes('/cliente')) return 'Company'
  if (p.includes('/fashion') || p.includes('/steel') || p.includes('/order') || p.includes('/pedido')) return 'Orders'
  if (p.includes('/dre-line')) return 'DreLine'
  if (p.includes('/financial') || p.includes('/financeiro')) return 'FinancialEntry'
  if (p.includes('/invoice') || p.includes('/nota-fiscal')) return 'Invoice'
  if (p.includes('/tax') || p.includes('/fiscal')) return 'TaxScenario'
  if (p.includes('/freight') || p.includes('/frete')) return 'Freight'
  if (p.includes('/router') || p.includes('/rota')) return 'Route'
  if (p.includes('/vehicle') || p.includes('/veiculo')) return 'Vehicle'
  if (p.includes('/equipment') || p.includes('/equipamento') || p.includes('/maquina') || p.includes('/machine')) return 'Machine'
  if (p.includes('/pipeline') || p.includes('/funil') || p.includes('/ticket')) return 'Pipeline'
  if (p.includes('/service') || p.includes('/servico') || p.includes('/stage') || p.includes('/etapa')) return 'Stage'
  return 'Stock'
}

const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  titleTooltip,
  tabs,
  activeTab,
  onTabChange,
  onReload,
  isLoading,
  filterHeaders,
  appliedFilters,
  onApplyFilter,
  onRemoveFilter,
  onClearAllFilters,
  onExport,
  exportLabel = 'Exportar excel',
  createButton,
  leftExtra,
  rightExtra,
  extraActions,
  tableName,
  moduleName,
  showAuditHeaderMenu = true,
  onOpenAutomations,
  hasTable,
  isTablePage,
}) => {
  const location = useLocation()
  const effectiveTableName = tableName || resolveTableFromPath(location.pathname)
  const navigate = useNavigate()
  const { clearSearch } = useSearch()
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false)
  const [isHeaderMenuOpen, setIsHeaderMenuOpen] = useState(false)
  const [isDeletedModalOpen, setIsDeletedModalOpen] = useState(false)
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false)
  const filterButtonRef = useRef<HTMLDivElement | null>(null)
  const headerMenuRef = useRef<HTMLDivElement | null>(null)
  const exportMenuRef = useRef<HTMLDivElement | null>(null)
  const exportContext = useTableExportContext()

  const isFormOrDetailRoute = useMemo(() => {
    const pathname = (location.pathname || '').toLowerCase()
    const formPatterns = [
      '/create',
      '/edit',
      '/novo',
      '/nova',
      '/editar',
      '/cadastrar',
      '/new',
    ]
    const hasFormPattern = formPatterns.some((pattern) => pathname.includes(pattern))
    const isViewDetailPattern = /\/(view|detail|detalhes)(\/|$)/.test(pathname)
    return hasFormPattern || isViewDetailPattern
  }, [location.pathname])

  const isFormOrDetailTitle = useMemo(() => {
    const t = (typeof title === 'string' ? title : '').toLowerCase().trim()
    return (
      t.startsWith('editar') ||
      t.startsWith('cadastrar') ||
      t.startsWith('novo ') ||
      t.startsWith('nova ') ||
      t.startsWith('criar ') ||
      t.startsWith('visualizar') ||
      t.startsWith('detalhes')
    )
  }, [title])

  const isTableScreen = useMemo(() => {
    if (hasTable !== undefined) return hasTable
    if (isTablePage !== undefined) return isTablePage

    if (isFormOrDetailRoute || isFormOrDetailTitle) {
      return false
    }

    if (exportContext?.hasTable) {
      return true
    }

    return Boolean(
      exportContext?.registration ||
      (filterHeaders && filterHeaders.length > 0) ||
      createButton ||
      onExport ||
      onReload
    )
  }, [
    hasTable,
    isTablePage,
    isFormOrDetailRoute,
    isFormOrDetailTitle,
    exportContext?.hasTable,
    exportContext?.registration,
    filterHeaders,
    createButton,
    onExport,
    onReload,
  ])

  const registerPageHeader = exportContext?.registerPageHeader
  const unregisterPageHeader = exportContext?.unregisterPageHeader

  useEffect(() => {
    if (!registerPageHeader || !unregisterPageHeader) return
    registerPageHeader()
    return () => unregisterPageHeader()
  }, [registerPageHeader, unregisterPageHeader])

  useEffect(() => {
    if (!isHeaderMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (headerMenuRef.current && !headerMenuRef.current.contains(e.target as Node)) {
        setIsHeaderMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isHeaderMenuOpen])

  useEffect(() => {
    if (!isExportMenuOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setIsExportMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isExportMenuOpen])

  const tableExport = exportContext?.registration ?? null

  const showTabs = !!tabs && tabs.length > 0
  const showFilters = !!filterHeaders && filterHeaders.length > 0 && !!onApplyFilter
  const showAppliedFilters =
    !!appliedFilters && appliedFilters.length > 0 && !!onRemoveFilter && !!onClearAllFilters

  return (
    <>
      <SubHeaderContainer>
        <LeftSide>
          <SectionName title={titleTooltip}>{title}</SectionName>
          {showTabs && (
            <TabsDiv>
              {tabs!.map((tab, index) => (
                <Tab
                  key={tab.key}
                  $isFirst={index === 0}
                  $isLast={index === tabs!.length - 1}
                  $isActive={tab.key === activeTab}
                  onClick={() => {
                    if (tab.key !== activeTab) {
                      onTabChange?.(tab.key)
                    }
                  }}
                >
                  {tab.icon &&
                    (typeof tab.icon === 'string' ? (
                      <img src={tab.icon} alt={`${tab.label}-icon`} />
                    ) : (
                      tab.icon
                    ))}
                  {tab.label}
                </Tab>
              ))}
            </TabsDiv>
          )}
          {leftExtra}
        </LeftSide>
        <RightSide>
          {rightExtra}
          {isTableScreen && onReload && (
            <RefreshIconContainer onClick={onReload} title="Atualizar">
              <RefreshIcon src={refreshAct} alt="Atualizar" $spinning={!!isLoading} />
            </RefreshIconContainer>
          )}
          {isTableScreen && showFilters && (
            <FilterWrapper>
              <OptionsButton ref={filterButtonRef} onClick={() => setIsFilterMenuOpen(true)}>
                <FilterIcon size={18} />
              </OptionsButton>
              {isFilterMenuOpen && (
                <FilterMenu
                  headers={filterHeaders!}
                  onApplyFilter={(filter) => {
                    onApplyFilter!(filter)
                    setIsFilterMenuOpen(false)
                  }}
                  onClose={() => setIsFilterMenuOpen(false)}
                  anchorRef={filterButtonRef}
                  filters={appliedFilters}
                />
              )}
            </FilterWrapper>
          )}

          {isTableScreen && showAuditHeaderMenu && (
            <HeaderMenuWrapper ref={headerMenuRef}>
              <OptionsButton
                onClick={() => setIsHeaderMenuOpen((prev) => !prev)}
                title="Mais opções"
              >
                <MoreVertical size={18} />
              </OptionsButton>
              {isHeaderMenuOpen && (
                <HeaderMenuDropdown>
                  <HeaderMenuItem
                    onClick={() => {
                      setIsHeaderMenuOpen(false)
                      setIsDeletedModalOpen(true)
                    }}
                  >
                    <Trash2 size={16} color="#ef4444" />
                    Auditoria (Excluídos)
                  </HeaderMenuItem>
                  <HeaderMenuItem
                    onClick={() => {
                      setIsHeaderMenuOpen(false)
                      if (onOpenAutomations) {
                        onOpenAutomations()
                      } else {
                        navigate('/panel/pipelines/automations')
                      }
                    }}
                  >
                    <Bot size={16} color="#0090D3" />
                    Automações
                  </HeaderMenuItem>
                </HeaderMenuDropdown>
              )}
            </HeaderMenuWrapper>
          )}

          {extraActions}
          {isTableScreen && (
            onExport ? (
              <ExportMenuWrapper ref={exportMenuRef}>
                <HeaderExportButton onClick={() => setIsExportMenuOpen((prev) => !prev)}>
                  {exportLabel || 'Exportar excel'}
                </HeaderExportButton>
                {isExportMenuOpen && (
                  <ExportDropdownMenu>
                    <ExportDropdownItem
                      onClick={async () => {
                        setIsExportMenuOpen(false)
                        await onExport('all')
                      }}
                    >
                      Exportar tudo
                    </ExportDropdownItem>
                    <ExportDropdownItem
                      onClick={async () => {
                        setIsExportMenuOpen(false)
                        await onExport('table')
                      }}
                    >
                      Exportar tabela
                    </ExportDropdownItem>
                  </ExportDropdownMenu>
                )}
              </ExportMenuWrapper>
            ) : tableExport ? (
              <ExportMenuWrapper ref={exportMenuRef}>
                <HeaderExportButton
                  onClick={() => setIsExportMenuOpen((prev) => !prev)}
                  disabled={tableExport.isExporting || tableExport.disabled}
                >
                  {tableExport.isExporting ? 'Exportando...' : (exportLabel || 'Exportar excel')}
                </HeaderExportButton>
                {isExportMenuOpen && (
                  <ExportDropdownMenu>
                    <ExportDropdownItem
                      onClick={async () => {
                        setIsExportMenuOpen(false)
                        if (tableExport.onExportAll) {
                          await tableExport.onExportAll()
                        } else {
                          await tableExport.onExport()
                        }
                      }}
                    >
                      Exportar tudo
                    </ExportDropdownItem>
                    <ExportDropdownItem
                      onClick={async () => {
                        setIsExportMenuOpen(false)
                        if (tableExport.onExportTable) {
                          await tableExport.onExportTable()
                        } else {
                          await tableExport.onExport()
                        }
                      }}
                    >
                      Exportar tabela
                    </ExportDropdownItem>
                  </ExportDropdownMenu>
                )}
              </ExportMenuWrapper>
            ) : null
          )}

          {isTableScreen && createButton && (
            <ButtonCreate onClick={createButton.onClick}>+ {createButton.label}</ButtonCreate>
          )}
        </RightSide>
      </SubHeaderContainer>
      {isTableScreen && showAppliedFilters && (
        <AppliedFiltersDisplay
          filters={appliedFilters!}
          onRemoveFilter={onRemoveFilter!}
          onClearAll={onClearAllFilters!}
        />
      )}

      {isDeletedModalOpen && (
        <DeletedRecordsModal
          tableName={effectiveTableName}
          moduleName={moduleName || title}
          onClose={() => setIsDeletedModalOpen(false)}
          onRecordRestored={() => onReload?.()}
        />
      )}
    </>
  )
}

export default PageHeader
